import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
import { formatPrice } from "../../utils/functions.js";
import placeholder from "../../assets/placeholder.svg";
import {
  FileText,
  Download,
  Eye,
  Calendar,
  DollarSign,
  X,
  Plus,
  Check,
  XCircle,
  Upload,
} from "lucide-react";
import PaymentPopup from "../common/PaymentPopup";

const BuyerPayment = () => {
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // all, paid, pending
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState("");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicesDetails, setInvoicesDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);

  // Fetch buyer invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get("/buyerinvoices/myinvoices");

        // Handle different response structures
        const invoicesData = response.data.data || [];

        console.log("API response data:", invoicesData);

        if (!Array.isArray(invoicesData)) {
          console.error("API response data is not an array:", invoicesData);
          setInvoices([]);
          return;
        }

        setInvoices(invoicesData);
      } catch (error) {
        console.error("Error fetching buyer invoices:", error);
        setError(translate("buyerInvoices.messages.noInvoicesFound"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchInvoices();
    }
  }, [user?.id]);

  // useEffect(() => {
  //   fetchInvoicesDetails(invoices?.[0]?.id);
  // }, [invoices]);

  // const fetchInvoicesDetails = async (invoiceId) => {
  //   const response = await api.get(`/buyerinvoicepayments/mypayments/${invoiceId}`);
  //   setInvoicesDetails(response.data.data);
  //   console.log(response.data.data);
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInvoiceStatus = (invoice) => {
    const status =
      invoice.invoicePaymentStatus?.name || invoice.invoicePaymentStatus;

    const statusConfig = {
      Paid: {
        text: translate("buyerInvoices.status.paid"),
        color: "text-white bg-green-700",
      },
      paid: {
        text: translate("buyerInvoices.status.paid"),
        color: "text-white bg-green-700",
      },
      Pending: {
        text: translate("buyerInvoices.status.pending"),
        color: "text-white bg-yellow-500",
      },
      pending: {
        text: translate("buyerInvoices.status.pending"),
        color: "text-white bg-yellow-500",
      },
      "Partial Payment": {
        text: translate("buyerInvoices.status.partialPayment"),
        color: "text-white bg-orange-700",
      },
      "partial-payment": {
        text: translate("buyerInvoices.status.partialPayment"),
        color: "text-white bg-orange-700",
      },
      Overdue: {
        text: translate("buyerInvoices.status.overdue"),
        color: "text-white bg-red-700",
      },
      overdue: {
        text: translate("buyerInvoices.status.overdue"),
        color: "text-white bg-red-700",
      },
      Approved: {
        text: translate("buyerInvoices.status.approved"),
        color: "text-white bg-green-700",
      },
      approved: {
        text: translate("buyerInvoices.status.approved"),
        color: "text-white bg-green-700",
      },
      Rejected: {
        text: translate("buyerInvoices.status.rejected"),
        color: "text-white bg-red-700",
      },
      rejected: {
        text: translate("buyerInvoices.status.rejected"),
        color: "text-white bg-red-700",
      },
      Cancelled: {
  text: translate("buyerInvoices.status.cancelled"),
  color: "text-white bg-red-500",
},
cancelled: {
  text: translate("buyerInvoices.status.cancelled"),
  color: "text-white bg-red-500",
},
    };

    return (
      statusConfig[status] || {
        text: translate("buyerInvoices.status.unknown"),
        color: "text-white bg-gray-700",
      }
    );
  };

  const filteredInvoices = invoices
    .filter((invoice) => {
      // Filter by status
      if (filterStatus !== "all") {
        const status =
          invoice.invoicePaymentStatus?.name || invoice.invoicePaymentStatus;

        // Map filter status to actual status values
        if (filterStatus === "paid") {
          if (
            !(
              status === "Paid" ||
              status === "paid" ||
              status === "Approved" ||
              status === "approved"
            )
          ) {
            return false;
          }
        } else if (filterStatus === "pending") {
          if (!(status === "Pending" || status === "pending")) {
            return false;
          }
        } else if (filterStatus === "partial-payment") {
          if (!(status === "Partial Payment" || status === "partial-payment")) {
            return false;
          }
        }
        else if (filterStatus === "cancelled") {
  if (!(status === "Cancelled" || status === "cancelled")) {
    return false;
  }
} 
         else if (status !== filterStatus) {
          return false;
        }
      }

      // Filter by invoice number search
      if (searchInvoiceNumber && searchInvoiceNumber.trim() !== "") {
        const invoiceNumber = invoice?.id?.toString() || "";
        return invoiceNumber
          .toLowerCase()
          .includes(searchInvoiceNumber.toLowerCase().trim());
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by updatedAt descending (newest first)
      const aDate = new Date(a?.updatedAt || 0);
      const bDate = new Date(b?.updatedAt || 0);

      return bDate - aDate; // Descending order (newest first)
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchInvoiceNumber]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleCloseModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  // Payment popup functions
  const handlePayNow = (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentPopup(true);
  };

  const handleClosePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedPaymentInvoice(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list after successful payment
    window.location.reload();
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-48 sm:h-64'>
        <div className='animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-[#f64f2d]'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='text-center py-8 sm:py-12 px-4'>
        <div className='text-red-600 mb-4 text-sm sm:text-base'>{error}</div>
        <button
          onClick={() => window.location.reload()}
          className='px-4 py-2 bg-[#f64f2d] text-white rounded-lg hover:bg-orange-600 text-sm sm:text-base'
        >
          {translate("buyerInvoices.buttons.close")}
        </button>
      </div>
    );
  }

  return (
    <div
      className='bg-[#FFF9F9] rounded-lg sm:rounded-xl shadow-md'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-lg sm:rounded-t-[16px] px-1 py-1'>
        <div className='p-4 sm:p-5 md:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
          <h2 className='text-base sm:text-lg md:text-xl font-semibold'>
            {translate("buyerInvoices.heading")}
          </h2>
        </div>
      </div>

      <div className='space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-5 py-3 sm:py-4'>
        {/* Filter Navigation */}
        <div className='border-b border-gray-200'>
          {/* Filter Tabs and Search */}
          <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 py-3 sm:py-4'>
            {/* Filter Tabs */}
            <nav className='flex space-x-1.5 sm:space-x-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide'>
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "all"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.all")} ({invoices.length})
              </button>
              <button
                onClick={() => setFilterStatus("paid")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "paid"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.paid")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return (
                      status === "Paid" ||
                      status === "paid" ||
                      status === "Approved" ||
                      status === "approved"
                    );
                  }).length
                }
                )
              </button>
              <button
                onClick={() => setFilterStatus("pending")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "pending"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.pending")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return status === "Pending" || status === "pending";
                  }).length
                }
                )
              </button>
              <button
                onClick={() => setFilterStatus("partial-payment")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "partial-payment"
                    ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.partialPayment")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return (
                      status === "Partial Payment" ||
                      status === "partial-payment"
                    );
                  }).length
                }
                )
              </button>
              <button
  onClick={() => setFilterStatus("cancelled")}
  className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
    filterStatus === "cancelled"
      ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
  }`}
>
   {translate("buyerInvoices.filters.cancelled")}(
  {
    invoices.filter((inv) => {
      const status =
        inv.invoicePaymentStatus?.name ||
        inv.invoicePaymentStatus;
      return status === "Cancelled" || status === "cancelled";
    }).length
  }
  )
</button>
            </nav>

            {/* Search Field */}
            <div className='flex items-center w-full lg:w-auto'>
              <div className='relative w-full lg:w-64 xl:w-80'>
                <input
                  type='text'
                  placeholder={translate("buyerInvoices.searchPlaceholder")}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 ${
                    isRTL ? "pr-9 sm:pr-10" : "pl-9 sm:pl-10"
                  } border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-xs sm:text-sm`}
                  value={searchInvoiceNumber || ""}
                  onChange={(e) => setSearchInvoiceNumber(e.target.value)}
                />
                <svg
                  className={`absolute ${
                    isRTL ? "right-2.5 sm:right-3" : "left-2.5 sm:left-3"
                  } top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              {searchInvoiceNumber && (
                <button
                  onClick={() => setSearchInvoiceNumber("")}
                  className='ml-2 p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors'
                  title='Clear search'
                >
                  <svg
                    className='w-4 h-4 sm:w-5 sm:h-5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Invoices List */}
        <div className='space-y-3 sm:space-y-4'>
          {paginatedInvoices.length === 0 ? (
            <div className='text-center py-8 sm:py-12'>
              <FileText className='w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4' />
              <h3 className='text-base sm:text-lg font-medium text-gray-900 mb-2'>
                {translate("buyerInvoices.messages.noInvoicesFound")}
              </h3>
              <p className='text-sm sm:text-base text-gray-500 px-4'>
                {filterStatus === "all"
                  ? translate("buyerInvoices.messages.noInvoicesYet")
                  : translate(
                      "buyerInvoices.messages.noFilteredInvoices"
                    ).replace(
                      "{status}",
                      translate(`buyerInvoices.filters.${filterStatus}`)
                    )}
              </p>
            </div>
          ) : (
            paginatedInvoices.map((invoice) => {
              const status = getInvoiceStatus(invoice);
              return (
                <div
                  key={invoice.id}
                  className='bg-white border border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:shadow-md transition-shadow'
                >
                  {/* Mobile Layout */}
                  <div className='block lg:hidden'>
                    {/* Header with Status */}
                    <div className='flex items-center justify-between mb-3 sm:mb-4'>
                      <div className='flex items-center space-x-2 sm:space-x-2.5 flex-1 min-w-0'>
                        <FileText className='w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0' />
                        <h3 className='text-sm sm:text-base font-semibold text-black truncate'>
                          {translate("buyerInvoices.labels.invoiceNumber")}{" "}
                          {invoice.id}
                        </h3>
                      </div>
                      <span
                        className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium flex-shrink-0 ${status.color}`}
                      >
                        {invoice?.invoicePaymentStatus?.name}
                      </span>
                    </div>

                    {/* Vehicle Image and Basic Info */}
                    <div className='flex items-start space-x-3 sm:space-x-4 mb-3 sm:mb-4'>
                      <div className='w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0'>
                        {invoice?.buyerInvoiceDetails?.[0]?.vehicle?.images?.[0]
                          ?.imageUrl && (
                          <img
                            src={
                              invoice.buyerInvoiceDetails[0].vehicle.images[0]
                                .imageUrl || placeholder
                            }
                            alt='Vehicle'
                            className='w-full h-full object-cover'
                          />
                        )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm sm:text-base font-semibold text-black leading-tight break-words'>
                          {invoice?.buyerInvoiceDetails?.[0]?.vehicle?.name ||
                            "-"}
                        </p>
                        <p className='text-xs sm:text-sm text-gray-500 mt-1'>
                          {translate("buyerInvoices.labels.lotNo")}{" "}
                          {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                            ?.lotNumber || "-"}
                        </p>
                      </div>
                    </div>

                    {/* Amount and Date Cards */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4'>
                      <div className='bg-gray-50 rounded-lg p-2.5 sm:p-3'>
                        <p className='text-xs sm:text-sm text-gray-500 mb-1.5'>
                          {translate("buyerInvoices.labels.totalAmount")}
                        </p>
                        <p className='text-sm sm:text-base font-semibold text-[#f64f2d]'>
                          {formatPrice(invoice?.totalAmount)}
                        </p>
                      </div>
                      <div className='bg-gray-50 rounded-lg p-2.5 sm:p-3'>
                        <p className='text-xs sm:text-sm text-gray-500 mb-1.5'>
                          {translate("buyerInvoices.labels.paidAmount")}
                        </p>
                        <p className='text-sm sm:text-base font-semibold text-green-700'>
                          {formatPrice(
                            invoice?.totalAmount - invoice?.remainingAmount
                          )}
                        </p>
                      </div>
                    </div>

                    <div className='bg-gray-50 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4'>
                      <p className='text-xs sm:text-sm text-gray-500 mb-1.5'>
                        {translate("buyerInvoices.labels.invoiceDate")}
                      </p>
                      <p className='text-xs sm:text-sm font-medium text-gray-900'>
                        {invoice?.createdAt
                          ? new Date(invoice.createdAt).toLocaleDateString(
                              undefined,
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )
                          : "-"}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className='flex flex-col gap-2 sm:gap-3'>
                      {/* Pay Now button for pending/partial payment invoices */}
                      {(status.text === "Pending" ||
                        status.text === "Partial Payment") && (
                        <button
                          onClick={() => handlePayNow(invoice)}
                          className='w-full bg-orange-500 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base font-medium shadow-sm'
                        >
                          {translate("buyerInvoices.buttons.payNow")}
                        </button>
                      )}

                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className={`flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 bg-[#f64f2d] text-white rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base font-medium shadow-sm ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <Eye
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${
                            isRTL ? "ml-2" : "mr-2"
                          }`}
                        />
                        {translate("buyerInvoices.buttons.viewDetails")}
                      </button>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className='hidden lg:block'>
                    <div className='flex items-start justify-between'>
                      {/* Invoice Details */}
                      <div className='flex-1'>
                        <div className='flex items-center space-x-3 mb-3'>
                          <div className='flex items-center space-x-2'>
                            <FileText className='w-4 h-4 text-gray-400' />
                            <h3 className='text-base font-semibold text-black'>
                              {translate("buyerInvoices.labels.invoiceNumber")}{" "}
                              {invoice.id}
                            </h3>
                          </div>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}
                          >
                            {invoice?.invoicePaymentStatus?.name}
                          </span>
                        </div>

                        <div className='mb-3'>
                          <div className='grid grid-cols-12 gap-4 items-center'>
                            {/* Vehicle Image */}
                            <div className='col-span-1 flex justify-start'>
                              <div className='w-12 h-12 bg-gray-200 rounded-md overflow-hidden flex-shrink-0'>
                                {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                  ?.images?.[0]?.imageUrl && (
                                  <img
                                    src={
                                      invoice.buyerInvoiceDetails[0].vehicle
                                        .images[0].imageUrl || placeholder
                                    }
                                    alt='Vehicle'
                                    className='w-full h-full object-cover'
                                  />
                                )}
                              </div>
                            </div>

                            {/* Vehicle Name */}
                            <div className='col-span-3 text-left'>
                              <p className='text-xs font-semibold text-black'>
                                {
                                  invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                    ?.name
                                }
                              </p>
                              <p className='text-xs font-semibold text-gray-500'>
                                {translate("buyerInvoices.labels.lotNo")}{" "}
                                {
                                  invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                    ?.lotNumber
                                }
                              </p>
                            </div>

                            {/* Total Amount */}
                            <div className='col-span-2 text-center'>
                              <p className='text-xs text-gray-500'>
                                {translate("buyerInvoices.labels.totalAmount")}
                              </p>
                              <p className='text-xs font-semibold text-[#f64f2d]'>
                                {formatPrice(invoice?.totalAmount)}
                              </p>
                            </div>

                            {/* Paid Amount */}
                            <div className='col-span-2 text-center'>
                              <p className='text-xs text-gray-500'>
                                {translate("buyerInvoices.labels.paidAmount")}
                              </p>
                              <p className='text-xs font-semibold text-green-700'>
                                {formatPrice(
                                  invoice?.totalAmount -
                                    invoice?.remainingAmount
                                )}
                              </p>
                            </div>

                            {/* Invoice Date */}
                            <div className='col-span-2 text-center'>
                              <p className='text-xs text-gray-500'>
                                {translate("buyerInvoices.labels.invoiceDate")}
                              </p>
                              <p className='text-xs font-medium text-gray-900'>
                                {invoice?.createdAt
                                  ? new Date(
                                      invoice.createdAt
                                    ).toLocaleDateString(undefined, {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    })
                                  : ""}
                              </p>
                            </div>

                            {/* Empty space for alignment on large screens */}
                            <div className='col-span-2'></div>
                          </div>
                        </div>

                        {/* Vehicle Information */}
                        {invoice?.vehicle && (
                          <div className='bg-gray-50 rounded-md p-3'>
                            <h4 className='font-medium text-gray-900 mb-1 text-sm'>
                              {translate("buyerInvoices.labels.vehicleDetails")}
                            </h4>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-1 text-xs'>
                              <div>
                                <span className='text-gray-500'>
                                  {translate("buyerInvoices.labels.vehicle")}
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-2" : "ml-2"
                                  } font-medium`}
                                >
                                  {invoice?.vehicle?.name}
                                </span>
                              </div>
                              <div>
                                <span className='text-gray-500'>
                                  {translate("buyerInvoices.labels.lotNumber")}
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-2" : "ml-2"
                                  } font-medium`}
                                >
                                  {invoice?.vehicle?.lotNumber}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className='flex flex-col space-y-1 ml-4'>
                        {/* Pay Now button for pending/partial payment invoices */}
                        {(status.text === "Pending" ||
                          status.text === "Partial Payment") && (
                          <button
                            onClick={() => handlePayNow(invoice)}
                            className='w-full bg-orange-500 text-white px-3 py-1.5 rounded-md hover:bg-orange-600 transition-colors text-sm'
                          >
                            <span>
                              {translate("buyerInvoices.buttons.payNow")}
                            </span>
                          </button>
                        )}

                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          className={`flex items-center justify-center px-3 py-1.5 bg-[#f64f2d] text-white rounded-md hover:bg-orange-600 transition-colors text-sm ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <Eye
                            className={`w-4 h-4 ${isRTL ? "ml-1" : "mr-1"}`}
                          />
                          <span>
                            {translate("buyerInvoices.buttons.viewDetails")}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex flex-col sm:flex-row items-center justify-between mt-4 sm:mt-6 gap-3 sm:gap-0'>
            <div
              className={`text-xs sm:text-sm text-gray-700 text-center ${
                isRTL ? "sm:text-right" : "sm:text-left"
              }`}
            >
              {translate("buyerInvoices.pagination.showing")} {startIndex + 1}{" "}
              {translate("buyerInvoices.pagination.to")}{" "}
              {Math.min(endIndex, filteredInvoices.length)}{" "}
              {translate("buyerInvoices.pagination.of")}{" "}
              {filteredInvoices.length}{" "}
              {translate("buyerInvoices.pagination.invoices")}
            </div>
            <div
              className={`flex items-center ${
                isRTL ? "space-x-reverse" : "space-x-1"
              } sm:space-x-2`}
            >
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className='px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {translate("buyerInvoices.pagination.previous")}
              </button>

              <div className='flex space-x-1 overflow-x-auto'>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md flex-shrink-0 ${
                        currentPage === page
                          ? "bg-[#f64f2d] text-white"
                          : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className='px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {translate("buyerInvoices.pagination.next")}
              </button>
            </div>
          </div>
        )}

        {/* Invoice Payment Modal */}
        {showInvoiceModal && selectedInvoice && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4'>
            <div className='bg-white rounded-lg sm:rounded-xl shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto'>
              {/* Header */}
              <div className='flex items-center justify-between p-4 sm:p-6 border-b'>
                <h3 className='text-base sm:text-lg md:text-xl font-semibold text-gray-900'>
                  {translate("buyerInvoices.labels.paymentHistory")} #
                  {selectedInvoice?.id}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className='text-gray-400 hover:text-gray-600 transition-colors p-1'
                >
                  <X className='w-5 h-5 sm:w-6 sm:h-6' />
                </button>
              </div>

              {/* Content */}
              <div className='p-4 sm:p-6'>
                {/* Invoice Summary Cards */}
                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6'>
                  <div className='bg-gray-50 rounded-lg p-3 sm:p-4'>
                    <h4 className='text-xs sm:text-sm font-semibold text-gray-500 mb-1.5 sm:mb-2'>
                      {translate("buyerInvoices.labels.totalAmount")}
                    </h4>
                    <p className='text-xl sm:text-2xl font-bold text-gray-900'>
                      {formatPrice(selectedInvoice?.totalAmount)}
                    </p>
                  </div>
                  <div className='bg-gray-50 rounded-lg p-3 sm:p-4'>
                    <h4 className='text-xs sm:text-sm font-semibold text-gray-500 mb-1.5 sm:mb-2'>
                      {translate("buyerInvoices.labels.remainingAmount")}
                    </h4>
                    <p className='text-xl sm:text-2xl font-bold text-red-600'>
                      {formatPrice(selectedInvoice?.remainingAmount)}
                    </p>
                  </div>
                </div>

                {/* Payment History */}
                <div className='mb-4 sm:mb-6'>
                  <div className='flex items-center justify-between mb-3 sm:mb-4'>
                    <h4 className='text-base sm:text-lg font-semibold text-gray-900'>
                      {translate("buyerInvoices.labels.paymentHistory")}
                    </h4>
                  </div>

                  {/* Payment History Table */}
                  <div className='overflow-x-auto -mx-4 sm:mx-0'>
                    <div className='inline-block min-w-full align-middle'>
                      <table className='min-w-full divide-y divide-gray-200'>
                        <thead className='bg-gray-50'>
                          <tr>
                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.paymentId")}
                            </th>
                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.paidAmount")}
                            </th>

                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.status")}
                            </th>
                            <th
                              className={`px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {translate("buyerInvoices.labels.invoiceDate")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className='bg-white divide-y divide-gray-200'>
                          {selectedInvoice ? (
                            <tr>
                              <td className='px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900'>
                                #{selectedInvoice?.invoicePaymentStatus?.id}
                              </td>

                              <td className='px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900'>
                                {formatPrice(
                                  selectedInvoice.totalAmount -
                                    selectedInvoice.remainingAmount
                                )}
                              </td>

                              <td className='px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap'>
                                <span
                                  className={`inline-flex px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                                    getInvoiceStatus(selectedInvoice).color
                                  }`}
                                >
                                  {selectedInvoice.invoicePaymentStatus?.name}
                                </span>
                              </td>
                              <td className='px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900'>
                                {formatDate(selectedInvoice.createdAt)}
                              </td>
                            </tr>
                          ) : (
                            <tr>
                              <td
                                colSpan='4'
                                className='px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-500'
                              >
                                {translate(
                                  "buyerInvoices.messages.noPaymentHistory"
                                )}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className='flex justify-end p-4 sm:p-6 border-t'>
                <button
                  onClick={handleCloseModal}
                  className='px-4 sm:px-6 py-2 bg-[#f64f2d] text-white rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base'
                >
                  {translate("buyerInvoices.buttons.close")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Popup */}
      <PaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePaymentPopup}
        selectedItem={selectedPaymentInvoice}
        itemType='invoice'
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default BuyerPayment;
